/** Paleta extra colorida e infantil para Edusico Junior. */
export const JUNIOR_COLORS = {
  cream: '#FFFBF0',
  paper: '#FFF8F5',
  ink: '#2A2540',
  inkMuted: '#6B6385',
  inkDim: '#A79FC0',

  violet: '#A855F7',
  violetSoft: '#F3E8FF',
  sky: '#0EA5E9',
  skySoft: '#E0F2FE',
  mint: '#10B981',
  mintSoft: '#D1FAE5',
  coral: '#F472B6',
  coralSoft: '#FCE7F3',
  sun: '#F59E0B',
  sunSoft: '#FEF3C7',
  lime: '#84CC16',
  limeSoft: '#ECFCCB',

  glass: 'rgba(255, 255, 255, 0.82)',
  glassBorder: 'rgba(168, 85, 247, 0.2)',
} as const;

export type JuniorAccent = 'violet' | 'sky' | 'mint' | 'coral' | 'sun' | 'lime';

export const ACCENT_HEX: Record<JuniorAccent, string> = {
  violet: JUNIOR_COLORS.violet,
  sky: JUNIOR_COLORS.sky,
  mint: JUNIOR_COLORS.mint,
  coral: JUNIOR_COLORS.coral,
  sun: JUNIOR_COLORS.sun,
  lime: JUNIOR_COLORS.lime,
};

export const ACCENT_SOFT: Record<JuniorAccent, string> = {
  violet: JUNIOR_COLORS.violetSoft,
  sky: JUNIOR_COLORS.skySoft,
  mint: JUNIOR_COLORS.mintSoft,
  coral: JUNIOR_COLORS.coralSoft,
  sun: JUNIOR_COLORS.sunSoft,
  lime: JUNIOR_COLORS.limeSoft,
};

export const HOME_THEME = {
  bg: JUNIOR_COLORS.paper,
  bgGradient:
    'radial-gradient(ellipse 90% 55% at 50% -10%, rgba(244, 114, 182, 0.35), transparent), radial-gradient(ellipse 70% 45% at 100% 100%, rgba(14, 165, 233, 0.3), transparent), radial-gradient(ellipse 60% 40% at 0% 90%, rgba(132, 204, 22, 0.25), transparent), #FFF8F5',
  text: JUNIOR_COLORS.ink,
  textGradient: 'linear-gradient(120deg, #F472B6 0%, #A855F7 35%, #0EA5E9 70%, #10B981 100%)',
  muted: JUNIOR_COLORS.inkMuted,
  dim: JUNIOR_COLORS.inkDim,
  accent: JUNIOR_COLORS.violet,
  accentSoft: JUNIOR_COLORS.violetSoft,
  highlight: JUNIOR_COLORS.sky,
  highlightSoft: JUNIOR_COLORS.skySoft,
  success: JUNIOR_COLORS.mint,
  warm: JUNIOR_COLORS.coral,
  cardBg: JUNIOR_COLORS.glass,
  cardBorder: JUNIOR_COLORS.glassBorder,
  glowViolet: '0 18px 48px -12px rgba(168, 85, 247, 0.35)',
  glowCoral: '0 18px 48px -12px rgba(244, 114, 182, 0.35)',
} as const;

export interface JuniorMenuItem {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  emoji: string;
  path: string;
  accent: JuniorAccent;
}

/** Menú principal — herramientas musicales. */
export const MENU_ITEMS: JuniorMenuItem[] = [
  {
    id: 'ritmo',
    title: 'Ritmo',
    subtitle: 'Percusión corporal',
    description: 'Atrapa el ritmo con palmas, patadas y chasquidos.',
    emoji: '🥁',
    path: '/ritmo',
    accent: 'coral',
  },
  {
    id: 'melodia',
    title: 'Melodía',
    subtitle: 'Cantar y tocar',
    description: 'Explora melodías sencillas y canciones infantiles.',
    emoji: '🎵',
    path: '/melodia',
    accent: 'violet',
  },
  {
    id: 'escuchar',
    title: 'Escuchar',
    subtitle: 'Oído musical',
    description: 'Escucha, identifica y describe sonidos musicales.',
    emoji: '👂',
    path: '/escuchar',
    accent: 'sky',
  },
  {
    id: 'mini-juegos',
    title: 'Mini juegos',
    subtitle: 'Diversión musical',
    description: 'Juegos cortos para aprender jugando.',
    emoji: '🎮',
    path: '/mini-juegos',
    accent: 'sun',
  },
  {
    id: 'herramientas',
    title: 'Herramientas',
    subtitle: 'Utilidades',
    description: 'Metrónomo, afinador y más recursos musicales.',
    emoji: '🛠️',
    path: '/herramientas',
    accent: 'lime',
  },
];

export interface JuniorHubItem {
  id: string;
  title: string;
  description: string;
  emoji: string;
  path: string;
  accent: JuniorAccent;
  available?: boolean;
}

export const RITMO_ITEMS: JuniorHubItem[] = [
  {
    id: 'atrapa-ritmo',
    title: 'Atrapa el ritmo',
    description: 'Toca los círculos de percusión corporal cuando caigan las burbujas.',
    emoji: '🎯',
    path: '/ritmo/atrapa-el-ritmo',
    accent: 'coral',
    available: true,
  },
  {
    id: 'eco-ritmico',
    title: 'Eco rítmico',
    description: 'Escucha un patrón con figuras rítmicas y repítelo con clap, caja o shaker.',
    emoji: '🔁',
    path: '/ritmo/eco-ritmico',
    accent: 'sun',
    available: true,
  },
  {
    id: 'boom-practica-ritmos',
    title: 'Práctica de ritmos',
    description: 'Marca sonidos y silencios con figuras en cada tiempo.',
    emoji: '🥁',
    path: '/ritmo/practica-ritmos',
    accent: 'coral',
    available: true,
  },
  {
    id: 'boom-descubre-ritmo',
    title: 'Descubre el ritmo',
    description: 'Escucha un patrón oculto y adivina las figuras.',
    emoji: '🔍',
    path: '/ritmo/descubre-el-ritmo',
    accent: 'mint',
    available: true,
  },
  {
    id: 'crea-ritmo',
    title: 'Crea tu ritmo',
    description: 'Arma tu propio patrón con percusión corporal.',
    emoji: '✨',
    path: '/ritmo/crea-ritmo',
    accent: 'mint',
    available: false,
  },
];

export const MELODIA_ITEMS: JuniorHubItem[] = [
  {
    id: 'constructor-melodias',
    title: 'Constructor de melodías',
    description: 'Arma tu melodía en el pentagrama con notas de colores.',
    emoji: '🎼',
    path: '/melodia/constructor',
    accent: 'violet',
    available: true,
  },
  {
    id: 'boom-patrones',
    title: 'Boom Patrones',
    description: 'Arma patrones de color con notas Do–Si y figuras rítmicas.',
    emoji: '🎨',
    path: '/melodia/boom-patrones',
    accent: 'violet',
    available: true,
  },
  {
    id: 'cling-canta',
    title: 'Aventura con Cling',
    description: 'Explora el mapa recogiendo tesoros o canta siguiendo las notas.',
    emoji: '🐦',
    path: '/melodia/cling-canta',
    accent: 'violet',
    available: true,
  },
  {
    id: 'canciones',
    title: 'Canciones',
    description: 'Melodías infantiles para cantar y tocar.',
    emoji: '🎶',
    path: '/melodia/canciones',
    accent: 'violet',
    available: false,
  },
];

export const ESCUCHAR_ITEMS: JuniorHubItem[] = [
  {
    id: 'sonidos',
    title: '¿Qué escucho?',
    description: 'Identifica instrumentos y sonidos del entorno.',
    emoji: '🔊',
    path: '/escuchar/sonidos',
    accent: 'sky',
    available: false,
  },
];

export const MINI_JUEGOS_ITEMS: JuniorHubItem[] = [
  {
    id: 'musical-cloud-tetris',
    title: 'Musical Cloud Tetris',
    description: 'Tetris con notas musicales: tríadas, tetracordes y escalas en la nube.',
    emoji: '☁️',
    path: '/mini-juegos/musical-cloud-tetris',
    accent: 'sun',
    available: true,
  },
];

export const HERRAMIENTAS_ITEMS: JuniorHubItem[] = [
  {
    id: 'metronomo',
    title: 'Metrónomo',
    description: 'Marca el pulso con un clic visual y sonoro.',
    emoji: '⏱️',
    path: '/herramientas/metronomo',
    accent: 'lime',
    available: false,
  },
];
